/* eslint-disable require-jsdoc */
/**
 * Http 类
 *
 * ## 自定义实例化配置项
 * - timeout - 设置请求超时时间，默认20000ms
 * - forceRequest - 在请求队列中，存在与上一个相同接口地址且相同的请求参数时，
 * 且上一个请求还未请求完成之前，是强制发起新请求还是过滤请求（默认过滤当前请求的发起），
 * 新请求最终调用老请求的结果
 * - abortRequest - 当满足forceRequest时，是否中断上一个请求，默认开启，中断后会返回强制发起的新接口的结果
 * - preventCatch - 当接口请求成功，发返回异常状态码后，错误会一直抛出，是否阻止进入业务自定义异常的逻辑处理，默认不阻止
 *
 * ## 特性
 * - [已完成]通过配置参数改变请求url中的变量
 * - [已完成]接口`请求成功`统一返回最终数据
 * - [已完成]接口`请求失败`统一toast
 * - [已完成]接口`请求成功，但返回错误信息时`统一toast
 * - [已完成]toast，可通过设置`showToast`关闭
 * - [已完成]请求自动加上默认query，可通过设置`appendParams`关闭
 * - [已完成]请求开始时自动加上loading动画，请求结束时自动关闭loading动画，可通过设置`showLoading`关闭
 * - [已完成]设置加上loading动画的显示延迟时间，可通过设置`loadingDelayTime`设置
 * - [已完成]设置loading组件类型：目前提供过场loading（可通过设置`loadingType`设置为'scene'，默认）和处理loading（设置为'process'）
 * - [已完成]设置`isSilent`静默模式，会同时关闭loading和toast
 * - [已完成]增加异常状态码的黑(codeBlackList)/白名单(codeWhiteList)管理
 *    - 存在白名单列表时，如果当前错误码在白名单中，则不会触发err-interceptor-abnormal里设置的拦截处理
 *    ，也不会触发最后的toast，不存在于白名单列表中的错误码均会触发错误
 *    - 存在黑名单列表时，只有当前错误码存在于黑名单时，才会触发err-interceptor-abnormal里设置的拦截处理
 *    ，其他不存在于黑名单中的错误码均不响应
 *    - 同时存在黑/白名单时，从白名单中从移除黑名单的项，之后的处理方式同只存在白名单
 *    - 不存在黑/白名单时，所有状态码都会进入err-interceptor-abnormal做默认处理
 * - [已完成]401权限的特殊处理(跳到无权限错误页)
 * - [放弃]相同接口请求在上一个请求还未返回续果时，拦截后续请求或者丢弃前面请求
 * -
 * - [已完成]改成类定义组织方式，提供自定义实例化配置
 *    -
 *
 * @example
 *
 * import { Http, default as http } from 'utils/http'
 *
 * // 1. 使用默认的http实例
 * http.get(url, option) 四种(get, post, put, delete)
 * http.get(option) 四种(get, post, put, delete)
 *
 * // 2. 实例化一个自定义的http模块
 * const newHttp = new Http({
 *   timeout: 5000 // 请求超时时间
 * })
 *
 * newHttp.get(url, option)
 * newHttp.get(option)
 *
 *
 * ## 更新日志
 * - [2017-08-22] 增加一些高级配置，如`forceRequest`,`abortRequest`,`preventCatch`,`preventCodeList`等
 *
 * @since 1.0.0
 * @version 1.2.0
 */

// 精简版
import _ from 'lodash'
import axios from 'axios'

import responseInterceptor from './response-interceptor'
import requestInterceptor from './request-interceptor'
import errorInterceptor from './error-interceptor'
import httpMetaOptionsHandler from './http-meta-options-handler'
import HTTP_CODE_MESSAGE from './code-message'
import httpLoading from './http-loading'
import userAgent from '@/utils/user-agent'
import { apiErrorARMSHandler, apiResponseErrorARMSHandler } from '@/utils/armsHandler'

const logger = console

// // 统一请求拦截器
// axios.interceptors.request.use(requestInterceptor, (err) => {
//   // 错误处理
//   return Promise.reject(err)
// })
//
// // 统一响应拦截器
// axios.interceptors.response.use(responseInterceptor, (err) => {
//   // 错误处理
//   return Promise.reject(err)
// })

// // 解析数据
axios.defaults.transformResponse = [
  function (data) {
    try {
      return JSON.parse(data)
    } catch (e) {
      return data
    }
  },
]

// 设置请求超时间为30s
axios.defaults.timeout = 30 * 1000

axios.defaults.baseURL = process.env.NODE_ENV === 'production' ? process.env.VUE_APP_API_HOST : '/api'

const errorStatusList = [0, 400, 404, 500, 502, 503, 504]

const methods = ['get', 'post', 'put', 'delete', 'head', 'options', 'patch']

const _actions = {
  /**
   * 构建异常错误实例
   *
   * @param {Error} err - 原错误实例
   * @param {object} config - 需要进行序列化的数据对象
   * @param {string} code - 状态码
   * @param {string} message - 描述文本
   * @returns {Error} 新的错误实例
   */
  generateResponseError(err, config, code, message) {
    let newError = new Error(message)
    // 解析data
    if (_.isString(config.data)) {
      config.data = JSON.parse(config.data)
    }
    if (userAgent.is('ios') && userAgent.osMajorVersion <= 9) {
      newError = err
    } else {
      Object.assign(newError, err)
    }
    // let c = code ?? err?.response?.data?.code
    let c = err?.response?.data?.code
    let m = message ?? err?.response?.data?.message
    if (!newError.response) {
      newError.response = {
        config,
        data: {
          code: c,
          message: m,
        },
      }
    } else {
      newError.response.data = {
        ...err?.response.data,
        code: c,
        message: m,
      }
    }

    return newError
  },
}

/**
 * Http 类
 *
 * @class Http
 * @param {object} method - 请求方法
 * @param {string} args.url - 请求地址
 * @param {string} args.options - 请求参数
 * @param {object} [args.options.vars] - 替换url请求变量的对象
 * @param {object} [args.options.params] - 放在url的query中的对象
 * @param {object} [args.options.data] - 用于post请求时payload对象
 * @param {object} [args.options.meta] - 自定义数据（如自定义配置是否显示静默不显示菊花，是否启用菊花延迟显示功能等功能）
 */
export class Http {
  $options = undefined // 配置选项

  /*
   * 构造函数
   * @constructor
   * @params {object} options - 配置参数
   */
  constructor(options = {}) {
    this.$options = Object.assign({}, Http.options, options)
  }

  /**
   * 配置参数
   *
   * @static
   * @enum
   */
  static options = {}

  /**
   * 更改默认配置参数
   *
   * @static
   * @param {object} [options={}] - 配置对象
   */
  static config(options = {}) {
    Object.assign(Http.options, options)
  }

  /**
   * http方法调用的统一封装
   *
   * @private
   * @param {string} method - 请求方法
   * @param {object} options - 请求配置
   * @returns {Promise} - 以Promise形式返回请求结果
   */
  _method(method, options) {
    options.method = method

    // 对配置参数进行初始化处理
    let mutationConfig = httpMetaOptionsHandler(options, this.$options)

    // 发起一个新的请求
    httpLoading.start(mutationConfig)

    return new Promise((resolve, reject) => {
      mutationConfig = requestInterceptor(mutationConfig)
      axios(mutationConfig)
        .then((body) => {
          // 附加meta，axios更新后，在request时加的config，没用了，需要再次附加一次
          body.config.meta = mutationConfig.meta
          body.config.vars = mutationConfig.vars
          // body = responseInterceptor(body)
          return resolve(body)
        })
        .catch((err) => {
          if (err.response) {
            err.response.config.meta = mutationConfig.meta
            err.response.config.vars = mutationConfig.vars
          }
          apiErrorARMSHandler(err)
          return reject(err)
        })
    })
      .then((body) => {
        if (body.config?.responseType === 'blob') {
          return body
        }
        // 请求成功，状态200，但后端是否有返回数据为空值，如果未返回，则返回空数据
        if (!body.data || body.data.code !== '00000') {
          apiResponseErrorARMSHandler(body)
          const err = new Error(body?.data?.message || 'Parse Error')
          err.response = body
          // 抛出异常请求被捕获
          return Promise.reject(err)
        }

        // 请求成功时，直接返回最终的数据
        return body.data['data']
      })
      .catch((err) => {
        let code = undefined
        let message = undefined
        const HTTP_ERROR_CODE = [403, 404, 405]
        const { response } = err
        // 未知类型常时
        if (response && HTTP_ERROR_CODE.includes(response.status)) {
          // 服务器4错误
          message = HTTP_CODE_MESSAGE[err.response.status] + ' ' + err.response.config.url
        } else if (err.message === 'Network Error') {
          // 网络异常错误异常
          code = 'C_NETWORK_1002'
          message = HTTP_CODE_MESSAGE[code] || 'Network Error'
        } else if (err.message === 'Parse Error') {
          console.log('========err============================')
          console.log(err)
          console.log('====================================')
          // 接口格式错误异常
          code = 'NETWORK_1001'
          message = HTTP_CODE_MESSAGE[code] || 'Parse Error'
        } else if (err.message.startsWith('timeout')) {
          // 请求超时异常
          code = 'REQUEST_1002'
          message = HTTP_CODE_MESSAGE[code] || 'Request Timeout'
        } else {
          message = err.response?.data.message || '系统异常,请联系管理员'
        }
        const newError = _actions.generateResponseError(err, mutationConfig, code, message)

        return Promise.reject(newError)
      })
      .finally(() => {
        // 请求时加载loading
        httpLoading.done(mutationConfig)
      })
      .catch((err) => {
        // 接口有返回，但非200时（存在response时）才进行处理
        if (err.response) {
          // logger.warn('err.response', err.response)
          // logger.warn('err.response.config', err.response.config)

          // 统一异常处理
          errorInterceptor(err.response)

          return Promise.reject(err.response)
        }

        // 这里再抛出错误时，可能就是一些语法上的错误了
        return Promise.reject(err)
      })
  }
}

// 在原型上绑定支持的方法
methods.forEach((method) => {
  Http.prototype[method] = function (...args) {
    return this._method(method, ...args)
  }
})

export default new Http()
